import { connect } from 'react-redux';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { Dispatch, SetStateAction, useState } from 'react';

import { IDictionaryEventTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryEventTypeSelectOption';
import { getEventBlockId } from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';

import { KnmCollapse } from '../../../KnmPackage/MainModule/components/KnmCollapse';
import { IInteractionCategorySelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IInteractionCategorySelectOption';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { IDecisionFormRef } from '../../MainModule/interfaces/IDecisionFormRef';
import { getDecisionFormRef } from '../../MainModule/selectors/decisionSelectors';
import { DecisionEventItemBody } from './DecisionEventItemBody';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';
import { removeLinkedDataByPassportEventGuid } from '../../../KnmErknmPackage/MainModule/common/cleanupLinkedViolationFormData';
import { ICommonFormRef } from '../../../KnmErknmPackage/MainModule/interfaces/ICommonFormRef';

interface IDecisionEventItemProps {
  watchOnly: boolean;
  interactionCategoryOptions: IInteractionCategorySelectOption[] | undefined;
  knmEventTypesSelectOptions: IDictionaryEventTypeSelectOption[] | undefined;
  uniqKnmEventTypesSelectOptions: IDictionaryEventTypeSelectOption[] | undefined;
  setUniqKnmEventTypesSelectOptions: Dispatch<SetStateAction<IDictionaryEventTypeSelectOption[] | undefined>>;
  decisionMinEventStartDateTime?: string;
}

interface IDecisionEventItemStoreProps {
  formRef: IDecisionFormRef | undefined;
}

const DecisionEventItemWithoutStore = ({
  watchOnly,
  interactionCategoryOptions,
  knmEventTypesSelectOptions,
  uniqKnmEventTypesSelectOptions,
  setUniqKnmEventTypesSelectOptions,
  formRef,
  decisionMinEventStartDateTime,
}: IDecisionEventItemProps & IDecisionEventItemStoreProps) => {
  const [knmCollapseOpened, setKnmCollapseOpened] = useState<{ [key: string]: boolean }>({});
  const remove = formRef?.current.mutators.remove;
  const makeRemoveEventHandler = (index: number) => () => {
    const events: IKnmErknmFormEvent[] | undefined = formRef?.current.getFieldState('events')?.value;

    if (!events || watchOnly || !remove) {
      return;
    }

    const removableType = events[index].type;
    const removableEventGuid = events[index].guid;
    const eventTypes = events.map(x => x.type);

    remove('events', index);

    // Паспортное действие ИР: чистим связанные videoFixations и events нарушений.
    removeLinkedDataByPassportEventGuid(formRef as ICommonFormRef | undefined, removableEventGuid, false, false, true);

    /**
     * Добавление удаленного типа Действия в список доступных для выбора
     *
     * Если список доступных типов не содержит удаляемый тип,
     * и если не остается после удаления ни одного Действия с таким типом - добавляем в список доступных
     */
    if (removableType) {
      if (
        !uniqKnmEventTypesSelectOptions?.find(x => x.title === removableType.title) &&
        eventTypes.filter(x => x?.title === removableType?.title)?.length <= 1
      ) {
        uniqKnmEventTypesSelectOptions?.push(removableType);
      }
      setUniqKnmEventTypesSelectOptions(uniqKnmEventTypesSelectOptions);
    }
  };

  return (
    <FieldArray name="events">
      {({ fields }: FieldArrayRenderProps<IKnmErknmFormEvent, HTMLElement>) => {
        if (!fields.length) {
          return null;
        }

        return (
          <>
            {fields.map((eventFieldName, sIndex) => {
              const fieldValue: IKnmErknmFormEvent = fields.value?.[sIndex];
              const id = getEventBlockId(fieldValue.listId);

              const hasError = checkHasErrorInCollapseBlock(formRef, eventFieldName);

              return (
                <KnmCollapse
                  id={id}
                  theme="fields"
                  title={`Действие №${sIndex + 1}`}
                  isNew={fieldValue.isNew}
                  opened={fieldValue.isNew}
                  onClose={!watchOnly || fieldValue.isNew ? makeRemoveEventHandler(sIndex) : undefined}
                  key={id}
                  changeOpenedCallback={e =>
                    setKnmCollapseOpened(state => ({
                      ...state,
                      [eventFieldName]: e,
                    }))
                  }
                  error={hasError && knmCollapseOpened[eventFieldName]}
                >
                  <DecisionEventItemBody
                    isWatchOnly={watchOnly}
                    interactionCategoryOptions={interactionCategoryOptions}
                    knmEventTypesSelectOptions={knmEventTypesSelectOptions}
                    setUniqKnmEventTypesSelectOptions={setUniqKnmEventTypesSelectOptions}
                    uniqKnmEventTypesSelectOptions={uniqKnmEventTypesSelectOptions}
                    eventFieldName={eventFieldName}
                    eventFieldValue={fieldValue}
                    eventId={id}
                    sIndex={sIndex}
                    decisionMinEventStartDateTime={decisionMinEventStartDateTime}
                  />
                </KnmCollapse>
              );
            })}
          </>
        );
      }}
    </FieldArray>
  );
};

const mapStateToProps = (state: IRootState): IDecisionEventItemStoreProps => ({
  formRef: getDecisionFormRef(state),
});

export const DecisionEventItem = connect(mapStateToProps)(DecisionEventItemWithoutStore);
